/**
 * ScoreCalculationSection — shows how raw score and optimal % were computed.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import type { FlowOptimizerItemPreview } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { useOptionalFlowOptimizerLivePreview } from '@/features/flow-optimizer/context/FlowOptimizerLivePreviewContext';
import { HoverSection } from '../hover-primitives';
import { fmtScore, fmtShare } from '../../format';
import type { PreviewRow } from '../../../preview-row.types';
import {
  buildRawScoreSteps,
  type AllocatorBounds,
  type CalcStep,
  type SubformulaInfo,
} from './score-calculation';
import { findSiblingRows } from './optimal-weight-steps';
import { buildWeightAwareSteps } from './weight-rule-steps';

const RAW_HINT = 'Each group: formula score × its group weight, then summed.';
const OPTIMAL_HINT =
  'Your weight = your share of the total score of the competing items in this ' +
  'pool (each item also keeps a small min-weight floor).';

// The bare "Score share" per-mille number stays hidden (it confused clients).
// The step-by-step "How the optimal % is calculated" block below is always
// shown for competing items — that is the readable score → % derivation.
const SHOW_SCORE_SHARE = false;

export interface ScoreCalculationSectionProps {
  row: PreviewRow;
  bounds: AllocatorBounds | null;
  previewItems: ReadonlyArray<FlowOptimizerItemPreview>;
  /** Subformula id -> {name, expression}, so `__sub_…` tokens render readably. */
  subFormulas?: SubformulaInfo;
}

const ScoreCalculationSection: FC<ScoreCalculationSectionProps> = ({
  row,
  bounds,
  previewItems,
  subFormulas,
}) => {
  const rawSteps = useMemo(
    () => buildRawScoreSteps(row, subFormulas),
    [row, subFormulas],
  );
  // A skipped group is simply ABSENT from the breakdown above, so without this
  // the popup shows re-shared weights that match no config screen and gives no
  // hint that a group was left out for this path only.
  const preview = useOptionalFlowOptimizerLivePreview();
  const skippedBy = row.pathBankId
    ? preview?.skippedGroups.get(row.pathBankId)
    : undefined;
  const optimalSteps = useMemo(() => {
    // Pass-through rows (excluded / inactive / no-formula) keep their current
    // weight and aren't competed for — the exclusion banner explains them.
    if (!bounds || row.passThrough) return [];
    return buildWeightAwareSteps(row, findSiblingRows(row, previewItems), bounds);
  }, [row, bounds, previewItems]);

  if (rawSteps.length === 0 && row.rawScore == null) return null;

  return (
    <HoverSection label="Score">
      <SummaryRow label="Raw score" value={fmtScore(row.rawScore)} />
      {rawSteps.length > 0 ? (
        <StepBlock title="How raw score is calculated" hint={RAW_HINT} steps={rawSteps} />
      ) : null}
      {skippedBy?.length ? <SkippedNote names={skippedBy} /> : null}
      {SHOW_SCORE_SHARE && !row.passThrough && row.scoreShare != null ? (
        <SummaryRow label="Score share" value={fmtShare(row.scoreShare)} />
      ) : null}
      {optimalSteps.length > 0 ? (
        <StepBlock
          title="How the optimal % is calculated"
          hint={OPTIMAL_HINT}
          steps={optimalSteps}
        />
      ) : null}
    </HoverSection>
  );
};

/** Names the groups left out of the breakdown, and why the survivors' weights
 *  do not match the Calculation tab's sliders for this path. */
const SkippedNote: FC<{ names: string[] }> = ({ names }) => (
  <p className="rounded border border-warning/40 bg-warning/10 px-2 py-1.5 text-[9px] leading-snug text-foreground">
    <span className="font-semibold">
      Skipped by {names.length === 1 ? 'this formula' : 'these formulas'}:
    </span>{' '}
    {names.join(', ')}. They are left out for this path only, so the formulas
    above share the full 100% here — which is why their weights differ from the
    Calculation tab.
  </p>
);

const SummaryRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-semibold tabular-nums text-foreground">{value}</span>
  </div>
);

const StepBlock: FC<{ title: string; steps: CalcStep[]; hint?: string }> = ({
  title,
  steps,
  hint,
}) => (
  <div className="rounded border border-border/60 bg-muted/20 px-2 py-1.5">
    <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </p>
    {hint ? (
      <p className="-mt-0.5 mb-1.5 text-[9px] leading-snug text-muted-foreground/80">{hint}</p>
    ) : null}
    <ul className="space-y-1">
      {steps.map((step) => (
        <li key={`${step.label}-${step.formula}`} className="space-y-0.5">
          <div className="flex items-baseline justify-between gap-2 text-[10px]">
            <span className="font-medium text-foreground">{step.label}</span>
            <span className="shrink-0 font-semibold tabular-nums text-primary">
              {step.result}
            </span>
          </div>
          <p className={cn('font-mono text-[9px] leading-snug text-muted-foreground')}>
            {step.formula}
          </p>
          {step.details?.length ? (
            <ul className="ml-1.5 space-y-0.5 border-l border-border/50 pl-2">
              {step.details.map((detail) => (
                <li
                  key={detail}
                  className="font-mono text-[9px] leading-snug text-muted-foreground/90"
                >
                  {detail}
                </li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  </div>
);

export default ScoreCalculationSection;
